<template>
  <v-app class="bsp-app">
    <header class="app-header">
      <nav class="app-header__inner" aria-label="Main navigation">
        <div class="app-brand">
          <a
            class="app-brand__logo"
            href="https://adereso.ai"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Go to the Adereso website"
          >
            <img src="/adereso-icon.svg" class="app-brand__icon" alt="" aria-hidden="true" />
            <span class="app-brand__wordmark">adereso</span>
          </a>
          <span class="app-brand__divider" aria-hidden="true"></span>
          <span class="app-brand__product">
            <small>WhatsApp Business</small>
            <strong>BSP Platform</strong>
          </span>
        </div>

        <div class="app-navigation" v-if="currentUser" aria-label="Platform sections">
          <AButton
            :color="isAccountsRoute ? 'blue' : 'base'"
            :variant="isAccountsRoute ? 'tonal' : 'text'"
            size="small"
            prepend-icon="tabler:brand-whatsapp"
            to="/whatsapp-business-accounts"
          >
            Accounts
          </AButton>
        </div>

        <div class="app-header__actions">
          <div class="user-status" v-if="currentUser" :title="currentUser.email">
            <span class="user-status__avatar" aria-hidden="true">
              {{ userInitial }}
            </span>
            <span class="user-status__email">{{ currentUser.email }}</span>
          </div>

          <AButton
            :icon="isDark ? 'tabler:sun' : 'tabler:moon'"
            variant="text"
            size="small"
            :help-text="isDark ? 'Use light theme' : 'Use dark theme'"
            :aria-label="isDark ? 'Use light theme' : 'Use dark theme'"
            @click="toggleTheme"
          />

          <AButton
            v-if="currentUser"
            color="red"
            variant="text"
            size="small"
            prepend-icon="tabler:logout"
            @click="signOut"
          >
            Sign out
          </AButton>
        </div>
      </nav>
    </header>

    <main id="content" class="app-content">
      <router-view v-if="isMounted" />
    </main>
  </v-app>
</template>

<script setup lang="ts">
import { AButton, useThemeConfig } from '@adereso-ai/vue-components'
import AuthService from './services/AuthService/AuthService'
import EstablishmentService from './services/EstablishmentService/EstablishmentService'
import { User } from './services/AuthService/types'
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useTheme } from 'vuetify'

const router = useRouter()
const vuetifyTheme = useTheme()
const { theme: componentTheme } = useThemeConfig()

const isMounted = ref(false)
const currentUser = ref<User | undefined>(undefined)
const isDark = computed(() => vuetifyTheme.global.current.value.dark)
const userInitial = computed(() => currentUser.value?.email?.charAt(0).toUpperCase() || 'A')
const currentPath = computed(() => router.currentRoute.value.path)
const isAccountsRoute = computed(() => currentPath.value.startsWith('/whatsapp-business-accounts'))

onMounted(async () => {
  componentTheme.value = isDark.value ? 'dark' : 'light'
  const authService = await AuthService.getInstance()
  currentUser.value = authService.currentUser.value
  watch(authService.currentUser, (newCurrentUser) => {
    currentUser.value = newCurrentUser
  })
  isMounted.value = true
})

function toggleTheme () {
  const nextTheme = isDark.value ? 'light' : 'dark'
  vuetifyTheme.global.name.value = nextTheme
  componentTheme.value = nextTheme
  window.localStorage.setItem('adereso-bsp-theme', nextTheme)
  document.documentElement.style.colorScheme = nextTheme
}

async function signOut () {
  // remove token from local storage
  const authService = await AuthService.getInstance()
  authService.signOut()
  const establishmentService = await EstablishmentService.getInstance()
  establishmentService.removeCurrentEstablishment()
  router.push('/')
}

</script>
